// The page tree (M7-02), adapted from Work OS `src/domain/doc-pages.ts` and `docs.ts`
// (`/Users/ac/Coding/Work OS/.worktrees/p19-spaces/app`, e0d760c1): ancestor chains and subtrees
// are walked cycle-safe; a page never moves under itself or one of its own pages; siblings keep an
// explicit order; a visible page under a parent the reader may not see floats to the top; an
// ancestor the reader may not see reads "…" in breadcrumbs. What changed: storage and transport
// (pure functions over rows here; the API locks and writes), and Work OS's per-page grants became
// CompanyOS's `restricted` flag, inherited by every page below a restricted one.

export type PageNode = {
  id: string;
  parentPageId: string | null;
  position: number;
  title: string;
  restricted?: boolean;
};

export const PAGE_COPY = {
  ownDescendant: "A page cannot move under itself or one of its own pages",
  otherSpace: "The parent page is in another space",
  archivedParent: "The parent page is archived",
  taskBody: "A task's page moves with its task",
} as const;

/** Ancestors of `parentId` (nearest first), complete and cycle-safe. */
export function ancestorsOf(
  byId: ReadonlyMap<string, Pick<PageNode, "id" | "parentPageId">>,
  parentId: string | null,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = parentId ? byId.get(parentId) : undefined;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.push(cur.id);
    cur = cur.parentPageId ? byId.get(cur.parentPageId) : undefined;
  }
  return out;
}

/** Every page below `id` (breadth first), cycle-safe; `id` itself excluded. */
export function descendantsOf(
  nodes: readonly Pick<PageNode, "id" | "parentPageId">[],
  id: string,
): string[] {
  const kids = new Map<string, string[]>();
  for (const n of nodes)
    if (n.parentPageId) kids.set(n.parentPageId, [...(kids.get(n.parentPageId) ?? []), n.id]);
  const out: string[] = [];
  const seen = new Set<string>([id]);
  const queue = [...(kids.get(id) ?? [])];
  while (queue.length > 0) {
    const next = queue.shift() as string;
    if (seen.has(next)) continue;
    seen.add(next);
    out.push(next);
    queue.push(...(kids.get(next) ?? []));
  }
  return out;
}

/** Whether placing `id` under `parentId` would put it under itself (Work OS `assertDocMove`). */
export function movesUnderItself(
  byId: ReadonlyMap<string, Pick<PageNode, "id" | "parentPageId">>,
  id: string,
  parentId: string | null,
): boolean {
  if (parentId === null) return false;
  return parentId === id || ancestorsOf(byId, parentId).includes(id);
}

/**
 * Sibling order (Work OS D140): `siblings` in their current order without `id`, then `id` at
 * `index` (clamped; default last). Returns the new order; positions are the indexes.
 */
export function placeAt(siblings: readonly string[], id: string, index?: number): string[] {
  const rest = siblings.filter((s) => s !== id);
  const at = index === undefined ? rest.length : Math.max(0, Math.min(index, rest.length));
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

/** Siblings in display order: `position`, then title, then id (stable for equal positions). */
export function bySiblingOrder<T extends Pick<PageNode, "id" | "position" | "title">>(
  a: T,
  b: T,
): number {
  return a.position - b.position || a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
}

export type TreeNode<T> = T & { children: TreeNode<T>[] };

/**
 * Nested by `parentPageId`, siblings in order. A page whose parent is not in `rows` (the reader
 * may not see it, or it is archived) floats to the top level (Work OS `docTreeWhere`).
 */
export function buildTree<T extends PageNode>(rows: readonly T[]): TreeNode<T>[] {
  const byId = new Map<string, TreeNode<T>>();
  for (const r of rows) byId.set(r.id, { ...r, children: [] });
  const roots: TreeNode<T>[] = [];
  for (const node of byId.values()) {
    const parent = node.parentPageId ? byId.get(node.parentPageId) : undefined;
    // A cycle in the stored rows cannot nest forever: a node already an ancestor goes to the top.
    if (parent && !ancestorsOf(byId, node.parentPageId).includes(node.id))
      parent.children.push(node);
    else roots.push(node);
  }
  const sort = (list: TreeNode<T>[]) => {
    list.sort(bySiblingOrder);
    for (const n of list) sort(n.children);
  };
  sort(roots);
  return roots;
}

/**
 * Breadcrumbs of a page, root first (Work OS `docContext`): an ancestor the reader may not view
 * reads `{ id: null, title: "…" }`, so a hidden title never shows.
 */
export function breadcrumbsOf(
  byId: ReadonlyMap<string, Pick<PageNode, "id" | "parentPageId" | "title">>,
  parentId: string | null,
  canView: (id: string) => boolean,
): { id: string | null; title: string }[] {
  return ancestorsOf(byId, parentId)
    .reverse()
    .map((id) => {
      const n = byId.get(id);
      return n && canView(id) ? { id, title: n.title } : { id: null, title: "…" };
    });
}

/**
 * The restriction rule (05 `page.restricted`, inherited like Work OS grants): a page under a
 * restricted parent is restricted; otherwise it keeps its own flag. A move or an archive never
 * widens who sees a page: a page leaving a restricted ancestor stays restricted.
 */
export function restrictedUnder(own: boolean, parentRestricted: boolean): boolean {
  return own || parentRestricted;
}
